import { createContext, useState, useMemo } from "react";
import { createTheme } from "@mui/material";

export const tokens = (mode: any) => ({
  ...(mode === "light"
    ? {
      primary: {
        "50": "#fff0f0",
        "100": "#ffdddd",
        "200": "#ffc0c0",
        "300": "#ff9494",
        "400": "#ff5757",
        "500": "#ff2323",
        "600": "#ff0000",
        "700": "#d70000",
        "800": "#b10303",
        "900": "#920a0a",
        "950": "#500000"
      },
      grey: {
        "50": "#f6f6f6",
        "100": "#e7e7e7",
        "200": "#d1d1d1",
        "300": "#b0b0b0",
        "400": "#888888",
        "500": "#6d6d6d",
        "600": "#5d5d5d",
        "700": "#4f4f4f",
        "800": "#454545",
        "900": "#3d3d3d",
        "950": "#282828"
      },
      background: "#FFFFFF",
      success: {
        DEFAULT: "#66bb6a",
        light: "#81c784",
        dark: "#388e3c"
      },
      warning: {
        DEFAULT: "#ffb74d",
        light: "#ffa726",
        dark: "#f57c00"
      },
      info: {
        DEFAULT: "#29b6f6",
        light: "#4fc3f7",
        dark: "#0288d1"
      },
      error: {
        DEFAULT: "#f44336",
        light: "#e57373",
        dark: "#d32f2f"
      }
    }
    : {
      primary: {
        "950": "#fff0f0",
        "900": "#ffdddd",
        "800": "#ffc0c0",
        "700": "#ff9494",
        "600": "#ff5757",
        "500": "#ff2323",
        "400": "#ff0000",
        "300": "#d70000",
        "200": "#b10303",
        "100": "#920a0a",
        "050": "#500000"
      },
      grey: {
        "50": "#f6f6f6",
        "100": "#e7e7e7",
        "200": "#d1d1d1",
        "300": "#b0b0b0",
        "400": "#888888",
        "500": "#6d6d6d",
        "600": "#5d5d5d",
        "700": "#4f4f4f",
        "800": "#454545",
        "900": "#3d3d3d",
        "950": "#282828"
      },
      background: "#282828",
      success: {
        DEFAULT: "#66bb6a",
        light: "#81c784",
        dark: "#388e3c"
      },
      warning: {
        DEFAULT: "#ffb74d",
        light: "#ffa726",
        dark: "#f57c00"
      },
      info: {
        DEFAULT: "#29b6f6",
        light: "#4fc3f7",
        dark: "#0288d1"
      },
      error: {
        DEFAULT: "#f44336",
        light: "#e57373",
        dark: "#d32f2f"
      }
    })
});

export const themeSettings = (mode: any) => {
  const colors: any = tokens(mode);
  return {
    palette: {
      mode: mode,
      ...(mode === "light"
        ? {
          primary: {
            main: colors.primary[500]
          },
          secondary: {
            main: colors.primary[200]
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[200]
          },
          background: {
            default: "#FFFFFF",
            main: "#FFFFFF"
          },
          running: {
            main: "#66bb6a" // Define running color
          }
        }
        : {
          primary: {
            main: colors.primary[500]
          },
          secondary: {
            main: colors.primary[200]
          },
          neutral: {
            dark: colors.grey[700],
            main: colors.grey[500],
            light: colors.grey[200]
          },
          background: {
            default: colors.grey[950]
          },
          running: {
            main: "#66bb6a" // Define running color
          }
        }),
      colors: colors
    },
    typography: {
      fontFamily: ["Roboto", "sans-serif"].join(","),
      fontSize: 14,
      p: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 14,
        fontWeight: 400
      },
      h1: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 48,
        fontWeight: 800
      },
      h2: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 36,
        fontWeight: 800
      },
      h3: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 24,
        fontWeight: 600
      },
      h4: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 20,
        fontWeight: 600
      },
      h5: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 18,
        fontWeight: 500
      },
      h6: {
        fontFamily: ["Roboto", "sans-serif"].join(","),
        fontSize: 16,
        fontWeight: 500
      }
    }
  };
};

// context for colors

export const ColorModeContext = createContext({
  toggleColorMode: (): any => {
  }
});

export const useMode = () => {
  const [mode, setMode] = useState("light");
  const colorMode = useMemo(
    (): any => ({
      toggleColorMode: () =>
        setMode((prev) => (prev === "light" ? "dark" : "light"))
    }),
    []
  );
  const theme = useMemo(() => createTheme(themeSettings(mode)), [mode]);
  return [theme, colorMode];
};
